import type { AddProjectMemberResult, ProjectMember } from '~/models'

/**
 * Email comparison for project members. The API echoes back whatever casing and
 * padding was submitted, and the invite form lower-cases its input, so raw
 * equality would miss a member that is genuinely already there.
 */
export function normalizeMemberEmail(email: string | undefined | null): string {
  return (email ?? '').trim().toLowerCase()
}

export function matchMemberByEmail(
  members: ProjectMember[],
  email: string,
): ProjectMember | null {
  const target = normalizeMemberEmail(email)
  if (!target)
    return null
  return (
    members.find(
      member => normalizeMemberEmail(member.memberEmail) === target,
    ) ?? null
  )
}

export interface InvitePostOutcome {
  data?: ProjectMember
  success: boolean
  statusCode?: number
  error?: unknown
}

export function getInviteFailureReason(error: unknown): 'recipient_not_found' | 'api_error' {
  const data = error !== null && typeof error === 'object' && 'data' in error
    ? error.data
    : error
  return data !== null && typeof data === 'object'
    && 'code' in data && data.code === 'recipient_not_found'
    ? 'recipient_not_found'
    : 'api_error'
}

/**
 * Whether the server acknowledged success but gave us nothing to show for it.
 *
 * This is the only case a follow-up read can settle: the 2xx is itself the
 * evidence that this attempt succeeded, so the lookup is just fetching the
 * member we were not handed.
 *
 * Deliberately excluded:
 *  - a received HTTP error status. The server reached a decision and reported
 *    it; re-reading the list would let a member invited earlier turn a genuine
 *    failure into apparent success, claiming an email that was never sent.
 *  - no response at all. Still ambiguous, but a later GET cannot prove *this*
 *    attempt created the member — a CORS failure may mean the POST never ran,
 *    and an older matching member produces the same false success. Confirming
 *    a lost-after-commit response needs real evidence: an idempotency key, a
 *    backend upsert, a pre-request member snapshot, or a server-generated
 *    operation id. The lookup alone is not that.
 */
export function isAmbiguousInviteOutcome(post: InvitePostOutcome): boolean {
  return post.success && !post.data
}

/**
 * Decides what an invite attempt achieved.
 *
 * `reconciled` is the member found by re-reading the list, or `null` when the
 * lookup found nothing or itself failed. It is only ever consulted for the
 * ambiguous outcomes above; a failed lookup must not change the original
 * outcome in either direction.
 */
export function resolveInviteResult(
  post: InvitePostOutcome,
  reconciled: ProjectMember | null,
): AddProjectMemberResult {
  if (post.success && post.data) {
    return { status: 'created', member: post.data }
  }
  if (reconciled && isAmbiguousInviteOutcome(post)) {
    return { status: 'reconciled', member: reconciled }
  }
  return { status: 'failed', statusCode: post.statusCode, error: post.error }
}

/**
 * Inserts or replaces a member by email rather than by id, because the list
 * also holds the project creator, which is synthesised locally without a real
 * member id, and a reconciled invite can return someone already present.
 */
export function upsertMemberByEmail(
  members: ProjectMember[],
  member: ProjectMember,
): ProjectMember[] {
  const target = normalizeMemberEmail(member.memberEmail)
  const index = members.findIndex(
    existing => normalizeMemberEmail(existing.memberEmail) === target,
  )
  if (index === -1) {
    return [...members, member]
  }
  const next = [...members]
  next[index] = member
  return next
}
